<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAI } from 'dashboard/composables/useAI';
import { useAlert } from 'dashboard/composables';
import ModalHeader from 'dashboard/components/ModalHeader.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Spinner from 'shared/components/Spinner.vue';

const emit = defineEmits(['close']);

const { t } = useI18n();
const { getSummary } = useAI();

const isLoading = ref(false);
const summary = ref('');
const hasError = ref(false);

const loadSummary = async () => {
  isLoading.value = true;
  hasError.value = false;
  summary.value = '';
  
  try {
    const result = await getSummary();
    
    if (result) {
      summary.value = result;
    } else {
      hasError.value = true;
      useAlert(t('INTEGRATION_SETTINGS.OPEN_AI.GENERATE_ERROR'));
    }
  } catch (error) {
    hasError.value = true;
    useAlert(t('INTEGRATION_SETTINGS.OPEN_AI.GENERATE_ERROR'));
  } finally {
    isLoading.value = false;
  }
};

const handleClose = () => {
  emit('close');
};

// Cargar el resumen al montar el componente
loadSummary();
</script>

<template>
  <div class="flex flex-col h-auto overflow-auto">
    <ModalHeader
      :header-title="$t('INTEGRATION_SETTINGS.OPEN_AI.CONVERSATION_SUMMARY.TITLE')"
      :header-content="$t('INTEGRATION_SETTINGS.OPEN_AI.CONVERSATION_SUMMARY.SUBTITLE')"
    />
    
    <div class="flex flex-col gap-4 p-8">
      <!-- Estado de carga -->
      <div v-if="isLoading" class="flex flex-col items-center justify-center gap-4 py-12">
        <Spinner size="large" />
        <p class="text-base text-n-slate-11">
          {{ $t('INTEGRATION_SETTINGS.OPEN_AI.CONVERSATION_SUMMARY.LOADING') }}
        </p>
      </div>

      <!-- Contenido del resumen -->
      <div
        v-else-if="summary && !hasError"
        class="p-4 rounded-lg bg-n-slate-2 border border-n-slate-6"
      >
        <p class="text-sm text-n-slate-12 whitespace-pre-wrap">
          {{ summary }}
        </p>
      </div>

      <!-- Estado de error -->
      <div
        v-else-if="hasError"
        class="flex flex-col items-center justify-center gap-4 py-12"
      >
        <fluent-icon
          icon="warning"
          size="32"
          class="text-n-red-9"
        />
        <p class="text-base text-n-slate-11">
          {{ $t('INTEGRATION_SETTINGS.OPEN_AI.CONVERSATION_SUMMARY.ERROR') }}
        </p>
      </div>
    </div>

    <!-- Footer con botones -->
    <div class="flex justify-end gap-2 px-8 py-4 border-t border-n-slate-6">
      <Button
        v-if="!isLoading"
        :label="$t('INTEGRATION_SETTINGS.OPEN_AI.CONVERSATION_SUMMARY.BUTTONS.CLOSE')"
        @click="handleClose"
      />
    </div>
  </div>
</template>

